/**
* Theme utility functions
*/

export interface ThemePalette {
    gradient: string
    panelBg: string
    panelSurface: string
    textPrimary: string
    textSecondary: string
    border: string
    assistantBubble: string
    userBubble: string
    inputBg: string
}

/**
* Convert hex color to RGB
*/
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null
}

/**
* Convert RGB to hex color
*/
function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')
}

/**
* Generate a harmonious secondary color from primary color
* Creates a gradient end color that complements the primary color
* Uses a simpler approach: shift hue and adjust lightness/saturation
*/
function generateSecondaryColor(primaryColor: string, theme: 'light' | 'dark'): string {
    const rgb = hexToRgb(primaryColor)
    if (!rgb) return theme === 'dark' ? '#0ea5e9' : '#a855f7' // Fallback to original

    const { r, g, b } = rgb

    // Convert RGB to HSL for easier manipulation
    const rNorm = r / 255
    const gNorm = g / 255
    const bNorm = b / 255

    const max = Math.max(rNorm, gNorm, bNorm)
    const min = Math.min(rNorm, gNorm, bNorm)
    let h = 0
    let s = 0
    const l = (max + min) / 2

    if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

        if (max === rNorm) {
            h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6
        } else if (max === gNorm) {
            h = ((bNorm - rNorm) / d + 2) / 6
        } else {
            h = ((rNorm - gNorm) / d + 4) / 6
        }
    }

    // Shift hue by ~30-60 degrees for a harmonious complementary color
    // Adjust based on theme: for dark theme, make it slightly lighter and more saturated
    // For light theme, make it slightly darker
    const hueShift = 0.15 // ~54 degrees shift (creates analogous/triadic harmony)
    const newH = (h + hueShift) % 1.0
    const newS = theme === 'dark' ? Math.min(s * 1.1, 0.85) : Math.min(s * 0.95, 0.75)
    const newL = theme === 'dark' ? Math.min(l + 0.1, 0.65) : Math.max(l - 0.08, 0.35)

    // Convert HSL back to RGB
    const c = (1 - Math.abs(2 * newL - 1)) * newS
    const x = c * (1 - Math.abs(((newH * 6) % 2) - 1))
    const m = newL - c / 2

    let rNew = 0
    let gNew = 0
    let bNew = 0

    const h6 = newH * 6
    if (h6 < 1) {
        rNew = c
        gNew = x
        bNew = 0
    } else if (h6 < 2) {
        rNew = x
        gNew = c
        bNew = 0
    } else if (h6 < 3) {
        rNew = 0
        gNew = c
        bNew = x
    } else if (h6 < 4) {
        rNew = 0
        gNew = x
        bNew = c
    } else if (h6 < 5) {
        rNew = x
        gNew = 0
        bNew = c
    } else {
        rNew = c
        gNew = 0
        bNew = x
    }

    rNew = Math.round((rNew + m) * 255)
    gNew = Math.round((gNew + m) * 255)
    bNew = Math.round((bNew + m) * 255)

    return rgbToHex(rNew, gNew, bNew)
}

/**
* Generate theme palette based on primary color and theme mode
* Automatically generates a complementary secondary color for gradients
* @param useGradient - If false, uses solid primary color instead of gradient
*/
export function generateThemePalette(
    primaryColor: string,
    theme: 'light' | 'dark',
    secondaryColor?: string, // Optional: allow manual override
    useGradient: boolean = true // Default: true (use gradient)
): ThemePalette {
    const base = primaryColor || '#6366f1'
    const panelBg = theme === 'dark' ? '#020617' : '#ffffff'
    const panelSurface = theme === 'dark' ? '#0f172a' : '#f8fafc'
    const textPrimary = theme === 'dark' ? '#f9fafb' : '#0f172a'
    const textSecondary = theme === 'dark' ? '#cbd5f5' : '#475569'
    const border = theme === 'dark' ? 'rgba(148, 163, 184, 0.35)' : 'rgba(15, 23, 42, 0.08)'
    // Assistant bubble: lighter in dark theme for better visibility against dark background
    // Using a lighter gray-blue that stands out from the dark panel surface
    const assistantBubble = theme === 'dark' ? 'rgba(51, 65, 85, 0.95)' : '#eef2ff'
    const inputBg = theme === 'dark' ? 'rgba(15, 23, 42, 0.7)' : '#ffffff'

    // Generate gradient or use solid color based on useGradient flag
    let gradient: string
    if (useGradient) {
        // Use provided secondary color or auto-generate from primary color
        const gradientEnd = secondaryColor || generateSecondaryColor(base, theme)
        gradient = `linear-gradient(135deg, ${base}, ${gradientEnd})`
    } else {
        // Use solid primary color
        gradient = base
    }

    return {
        gradient,
        panelBg,
        panelSurface,
        textPrimary,
        textSecondary,
        border,
        assistantBubble,
        userBubble: base,
        inputBg,
    }
}
